/**
 * Desktop topbar — breadcrumbs · search · + New.
 *
 * The search input is a cross-app typeahead: as the user types, it
 * fans out across notes / tasks / events / habits / shopping /
 * recipes via ``useMentionSearch`` and renders a result list. Picking
 * a result navigates to its canonical URL via ``mentionTo``.
 *
 * The ``+ New`` button reads the per-screen registered handler from
 * ``NewActionContext``; hidden when none is registered.
 */

import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";

import { useT } from "../lib/i18n";
import { Icon } from "../lib/icons";
import {
  KIND_ACCENT,
  KindIcon,
  MENTION_TO_ENTITY,
} from "../lib/entityKindVisuals";
import { useMentionSearch } from "../lib/useMentionSearch";
import { useCurrentNewAction } from "./NewActionContext";
import { mentionTo } from "./mentionTo";
import type { AppMeta } from "./apps";

interface DesktopTopbarProps {
  appMeta: AppMeta;
  /** Optional drilldown crumb (e.g. note id when a note is open). */
  crumb?: string | undefined;
}

export function DesktopTopbar({
  appMeta,
  crumb,
}: DesktopTopbarProps): JSX.Element {
  const [, T] = useT();
  const newAction = useCurrentNewAction();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);

  const { results, isLoading } = useMentionSearch(query);
  const showPopover = open && query.trim().length > 0;

  useEffect(() => {
    setHighlightIdx(0);
  }, [results]);

  function go(target: { kind: Parameters<typeof mentionTo>[0]; id: string }): void {
    void navigate(mentionTo(target.kind, target.id));
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (!showPopover) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (results.length > 0) {
        e.preventDefault();
        const target = results[Math.min(highlightIdx, results.length - 1)];
        if (target !== undefined) go(target);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  function onBlur(): void {
    // Delay so click handlers on result rows fire first.
    blurTimerRef.current = setTimeout(() => setOpen(false), 150);
  }

  function onFocus(): void {
    if (blurTimerRef.current !== null) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setOpen(true);
  }

  return (
    <div className="vz-topbar">
      <div className="vz-bread">
        <span>vuzlyk</span>
        <span className="vz-bread-sep">/</span>
        <span style={{ color: "var(--fg-1)" }}>{T(appMeta.i18nKey)}</span>
        {crumb !== undefined && crumb !== "" && (
          <>
            <span className="vz-bread-sep">/</span>
            <span style={{ color: "var(--fg-2)" }}>{crumb}</span>
          </>
        )}
      </div>
      <span style={{ flex: 1 }} />
      <div
        className="vz-topbar-search"
        style={{ position: "relative", width: 280 }}
      >
        <label
          htmlFor="vz-topbar-search-input"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 8px",
            border: "1px solid var(--line-soft)",
            borderRadius: 4,
            background: "var(--bg-1)",
            height: 28,
            boxSizing: "border-box",
            color: "var(--fg-2)",
            cursor: "text",
          }}
        >
          <Icon.search width={14} height={14} style={{ flexShrink: 0 }} />
          <input
            id="vz-topbar-search-input"
            ref={inputRef}
            type="search"
            className="vz-topbar-search-input"
            value={query}
            placeholder={T("topbar.searchPlaceholder")}
            aria-label={T("topbar.searchPlaceholder")}
            aria-autocomplete="list"
            aria-expanded={showPopover}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            style={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              background: "transparent",
              padding: 0,
              margin: 0,
              fontSize: 13,
              fontFamily: "inherit",
              color: "var(--fg-1)",
              lineHeight: "18px",
            }}
          />
        </label>

        {showPopover && (
          <div
            role="listbox"
            onMouseDown={(e) => e.preventDefault()}
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              right: 0,
              left: 0,
              maxHeight: 320,
              overflowY: "auto",
              background: "var(--bg-1)",
              border: "1px solid var(--line-soft)",
              borderRadius: 4,
              boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
              zIndex: 30,
            }}
          >
            {isLoading && (
              <div
                style={{
                  padding: "8px 10px",
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--fg-3)",
                }}
              >
                {T("loading")}
              </div>
            )}
            {!isLoading && results.length === 0 && (
              <div
                style={{
                  padding: "8px 10px",
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--fg-3)",
                }}
              >
                {T("mention.empty")}
              </div>
            )}
            {!isLoading &&
              results.map((r, idx) => {
                const entityKind = MENTION_TO_ENTITY[r.kind];
                return (
                  <button
                    key={`${r.kind}:${r.id}`}
                    type="button"
                    role="option"
                    aria-selected={idx === highlightIdx}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    onClick={() => go(r)}
                    className="vz-ef-link"
                    style={{
                      ["--cc" as string]: KIND_ACCENT[entityKind],
                      width: "100%",
                      boxSizing: "border-box",
                      cursor: "pointer",
                      border: "none",
                      borderLeft: `3px solid ${KIND_ACCENT[entityKind]}`,
                      background:
                        idx === highlightIdx
                          ? "var(--bg-2)"
                          : "transparent",
                      textAlign: "left",
                    }}
                  >
                    <span className="lk-icon" aria-hidden="true">
                      <KindIcon kind={entityKind} />
                    </span>
                    <span className="lk-name">{r.title}</span>
                    <span className="lk-meta">
                      {T(`mention.kind.${r.kind}`)}
                    </span>
                  </button>
                );
              })}
          </div>
        )}
      </div>
      {newAction !== null && (
        <button
          type="button"
          className="vz-btn vz-btn-primary"
          onClick={newAction.handler}
        >
          <Icon.plus /> {newAction.label}
        </button>
      )}
    </div>
  );
}
